import {useCallback, useEffect, useState} from 'react'
import {formatDateTimeToDDMMYYYY} from '../../common-function/formatDateToYearMonth'
import axios from 'axios'
import {PageTitle} from '../../../_metronic/layout/core'
import {KTIcon} from '../../../_metronic/helpers'
import {apiUrl} from '../../../api-utils/constantVariables'
import {AdminAuth} from '../../modules/auth/core/AdminAuth'

type AuditItem = {
  id: number
  admin_id: number | null
  email_attempted: string | null
  action: string
  success: boolean
  ip: string | null
  user_agent: string | null
  meta: Record<string, unknown> | null
  createdAt: string
}

const AuditLogsWrapper = () => {
  const [loading, setLoading] = useState(true)
  const [items, setItems] = useState<AuditItem[]>([])
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [actionFilter, setActionFilter] = useState('')
  const [successFilter, setSuccessFilter] = useState<'all' | 'ok' | 'fail'>('all')

  const limit = 50

  const load = useCallback(
    async (forcedPage?: number) => {
      const token = AdminAuth()
      if (!token) return
      const effectivePage = typeof forcedPage === 'number' ? forcedPage : page
      setLoading(true)
      try {
        const params: Record<string, string | number | undefined> = {
          page: effectivePage,
          limit,
        }
        const a = actionFilter.trim()
        if (a) params.action = a
        if (successFilter === 'ok') params.success = 'true'
        if (successFilter === 'fail') params.success = 'false'
        const res = await axios.get(`${apiUrl}/admin/audit-logs`, {
          headers: {'x-access-token': token},
          params,
        })
        const d = res.data?.data
        setItems(Array.isArray(d?.items) ? d.items : [])
        setTotal(typeof d?.pager?.total === 'number' ? d.pager.total : 0)
      } catch {
        setItems([])
        setTotal(0)
      } finally {
        setLoading(false)
      }
    },
    [page, actionFilter, successFilter, limit]
  )

  useEffect(() => {
    void load()
  }, [load])

  const totalPages = Math.max(1, Math.ceil(total / limit))

  const formatMeta = (m: Record<string, unknown> | null) => {
    if (!m || Object.keys(m).length === 0) return '—'
    try {
      const s = JSON.stringify(m)
      return s.length > 120 ? `${s.slice(0, 117)}…` : s
    } catch {
      return '—'
    }
  }

  return (
    <>
      <PageTitle breadcrumbs={[]}>Audit logs</PageTitle>
      <div className='card'>
        <div className='card-header border-0 pt-6'>
          <div className='card-title'>
            <div className='d-flex flex-wrap gap-3 align-items-center'>
              <div className='d-flex align-items-center position-relative my-1'>
                <KTIcon iconName='filter' className='fs-3 position-absolute ms-5' />
                <input
                  type='text'
                  className='form-control form-control-solid w-250px ps-13'
                  placeholder='Filter by action'
                  value={actionFilter}
                  onChange={(e) => setActionFilter(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setPage(1)
                      void load(1)
                    }
                  }}
                />
              </div>
              <select
                className='form-select form-select-solid w-auto'
                value={successFilter}
                onChange={(e) => {
                  setSuccessFilter(e.target.value as 'all' | 'ok' | 'fail')
                  setPage(1)
                }}
              >
                <option value='all'>All outcomes</option>
                <option value='ok'>Success only</option>
                <option value='fail'>Failed only</option>
              </select>
              <button
                type='button'
                className='btn btn-light-primary'
                onClick={() => {
                  setPage(1)
                  void load(1)
                }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
        <div className='card-body py-4'>
          {loading ? (
            <div className='d-flex flex-center py-10'>
              <span className='spinner-border spinner-border-sm text-primary' />
            </div>
          ) : (
            <div className='table-responsive'>
              <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                <thead>
                  <tr className='fw-bold text-muted'>
                    <th className='min-w-140px'>Time</th>
                    <th className='min-w-120px'>Action</th>
                    <th className='min-w-80px'>OK</th>
                    <th className='min-w-80px'>Admin ID</th>
                    <th className='min-w-160px'>Email</th>
                    <th className='min-w-120px'>IP</th>
                    <th className='min-w-200px'>Meta</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={7} className='text-muted text-center py-8'>
                        No audit entries found.
                      </td>
                    </tr>
                  ) : (
                    items.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <span className='text-gray-800 fw-semibold d-block fs-7'>
                            {formatDateTimeToDDMMYYYY(row.createdAt)}
                          </span>
                        </td>
                        <td>
                          <span className='text-gray-700'>{row.action}</span>
                        </td>
                        <td>
                          {row.success ? (
                            <span className='badge badge-light-success'>Yes</span>
                          ) : (
                            <span className='badge badge-light-danger'>No</span>
                          )}
                        </td>
                        <td>{row.admin_id ?? '—'}</td>
                        <td>
                          <span className='text-gray-700 fs-7'>{row.email_attempted || '—'}</span>
                        </td>
                        <td>
                          <span className='text-gray-700 fs-7'>{row.ip || '—'}</span>
                        </td>
                        <td>
                          <code className='fs-8 text-gray-600'>{formatMeta(row.meta)}</code>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
          {total > limit ? (
            <div className='d-flex flex-stack flex-wrap pt-6'>
              <div className='fs-7 text-gray-600'>
                Page {page} of {totalPages} ({total} total)
              </div>
              <div className='d-flex gap-2'>
                <button
                  type='button'
                  className='btn btn-sm btn-light'
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </button>
                <button
                  type='button'
                  className='btn btn-sm btn-light'
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </>
  )
}

export default AuditLogsWrapper
